import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/map';
import { UnifiedBackgroundCheckSource } from '../../core/interfaces/unified-background-check.interface';

@Injectable()
export class DriverBackgroundCheckStateService implements OnDestroy {
  private _driverId = new BehaviorSubject<number>(null);
  private _expanded = new BehaviorSubject<boolean>(false);
  private _isMaskPIIActive = new BehaviorSubject<boolean>(true);
  private _source = new BehaviorSubject<UnifiedBackgroundCheckSource>(null);
  private _ssnPreMasked = new BehaviorSubject<boolean>(false);
  private _runCheckVisible = new BehaviorSubject<boolean>(true);
  private _reportSelectorVisible = new BehaviorSubject<boolean>(true);

  private _reportDateChangeRequested = new Subject<string>();
  private _runCheckRequested = new Subject<void>();
  private _downloadRequested = new Subject<void>();
  private _activeStateChangeRequested = new Subject<void>();
  private _sectionSelectRequested = new Subject<string>();
  private _deactivateConfirmRequested = new Subject<string>();
  private _deactivateCancelRequested = new Subject<void>();

  driverId$: Observable<number> = this._driverId.asObservable();
  expanded$: Observable<boolean> = this._expanded.asObservable();
  isMaskPIIActive$: Observable<boolean> = this._isMaskPIIActive.asObservable();
  source$: Observable<UnifiedBackgroundCheckSource> = this._source.asObservable();
  ssnPreMasked$: Observable<boolean> = this._ssnPreMasked.asObservable();
  runCheckVisible$: Observable<boolean> = this._runCheckVisible.asObservable();
  reportSelectorVisible$: Observable<boolean> = this._reportSelectorVisible.asObservable();

  piiToggleLabel$: Observable<string> = this.isMaskPIIActive$.map((active) => (active ? 'Reveal PII' : 'Hide PII'));
  criminalCardsOpenByDefault$: Observable<boolean> = this.source$.map((source) => source !== 'SearchBug');

  reportDateChangeRequested$: Observable<string> = this._reportDateChangeRequested.asObservable();
  runCheckRequested$: Observable<void> = this._runCheckRequested.asObservable();
  downloadRequested$: Observable<void> = this._downloadRequested.asObservable();
  activeStateChangeRequested$: Observable<void> = this._activeStateChangeRequested.asObservable();
  sectionSelectRequested$: Observable<string> = this._sectionSelectRequested.asObservable();
  deactivateConfirmRequested$: Observable<string> = this._deactivateConfirmRequested.asObservable();
  deactivateCancelRequested$: Observable<void> = this._deactivateCancelRequested.asObservable();

  setDriverId(id: number): void {
    this._driverId.next(id);
  }

  setExpanded(expanded: boolean): void {
    this._expanded.next(expanded);
  }

  setMaskPIIActive(active: boolean): void {
    this._isMaskPIIActive.next(active);
  }

  toggleMaskPIIActive(): void {
    this._isMaskPIIActive.next(!this._isMaskPIIActive.getValue());
  }

  setSource(source: UnifiedBackgroundCheckSource): void {
    this._source.next(source);
  }

  setSsnPreMasked(preMasked: boolean): void {
    this._ssnPreMasked.next(preMasked);
  }

  setRunCheckVisible(visible: boolean): void {
    this._runCheckVisible.next(visible);
  }

  setReportSelectorVisible(visible: boolean): void {
    this._reportSelectorVisible.next(visible);
  }

  requestReportDateChange(date: string): void {
    this._reportDateChangeRequested.next(date);
  }

  requestRunCheck(): void {
    this._runCheckRequested.next();
  }

  requestDownload(): void {
    this._downloadRequested.next();
  }

  requestActiveStateChange(): void {
    this._activeStateChangeRequested.next();
  }

  requestSectionSelect(section: string): void {
    this._sectionSelectRequested.next(section);
  }

  requestDeactivateConfirm(reason: string): void {
    this._deactivateConfirmRequested.next(reason);
  }

  requestDeactivateCancel(): void {
    this._deactivateCancelRequested.next();
  }

  ngOnDestroy(): void {
    this._driverId.complete();
    this._expanded.complete();
    this._isMaskPIIActive.complete();
    this._source.complete();
    this._ssnPreMasked.complete();
    this._runCheckVisible.complete();
    this._reportSelectorVisible.complete();
    this._reportDateChangeRequested.complete();
    this._runCheckRequested.complete();
    this._downloadRequested.complete();
    this._activeStateChangeRequested.complete();
    this._sectionSelectRequested.complete();
    this._deactivateConfirmRequested.complete();
    this._deactivateCancelRequested.complete();
  }
}



// WEBPACK FOOTER //
// ./src/app/utils/driver-background-check/driver-background-check-state.service.ts